import WrapTextRounded from '@mui/icons-material/WrapTextRounded';
import { GridColumnMenuItemProps, useGridApiContext, useGridRootProps } from '@mui/x-data-grid-premium';
import React, { ChangeEvent } from 'react';
import { KEY_CREDITS_WRAP_LIMIT } from '../../..';
import { useI18n } from '../../../../i18n/I18n';
import { KEY_CREDITS_COLUMN_MENU_SELECT_MIN_WIDTH, KEY_CREDITS_DISPLAY_MODE } from '../../../constants';
import type { KeyCreditsDisplayMode, KeyCreditsWrapLimit } from '../../../types';
import {
    clampKeyCreditsWrapLimit,
    isKeyCreditsColumnField,
    KEY_CREDITS_COLUMN_MENU_WRAP_LIMIT_OPTIONS,
    stopKeyCreditsColumnMenuItemClick,
    stopKeyCreditsColumnMenuSelectKeyDown
} from '../../utils';

export interface KeyCreditsColumnMenuWrapLimitItemProps extends GridColumnMenuItemProps {
    displayModeColumnNames: string[];
    displayMode: Record<string, KeyCreditsDisplayMode>;
    wrapLimit: Record<string, KeyCreditsWrapLimit>;
    onWrapLimitChange: (field: string, value: KeyCreditsWrapLimit) => void;
}

export default function KeyCreditsColumnMenuWrapLimitItem({
    colDef,
    displayModeColumnNames,
    displayMode,
    wrapLimit,
    onWrapLimitChange
}: KeyCreditsColumnMenuWrapLimitItemProps) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const t = useI18n();
    const fieldName = colDef?.field ?? '';

    if (!isKeyCreditsColumnField(fieldName, displayModeColumnNames)) {
        return null;
    }

    const currentDisplayMode = displayMode[fieldName] ?? KEY_CREDITS_DISPLAY_MODE.lineBreak;
    if (currentDisplayMode === KEY_CREDITS_DISPLAY_MODE.chip) {
        return null;
    }

    const currentWrapLimit = clampKeyCreditsWrapLimit(wrapLimit[fieldName] ?? KEY_CREDITS_WRAP_LIMIT.default);
    const isBaseSelectNative = rootProps.slotProps?.baseSelect?.native ?? false;

    const handleWrapLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedWrapLimit = clampKeyCreditsWrapLimit(Number(event.target?.value));
        if (selectedWrapLimit === currentWrapLimit) return;
        onWrapLimitChange(fieldName, selectedWrapLimit);
        apiRef.current.hideColumnMenu();
    };

    return (
        <rootProps.slots.baseMenuItem
            onClick={stopKeyCreditsColumnMenuItemClick}
            iconStart={<WrapTextRounded fontSize="small" />}
        >
            <rootProps.slots.baseSelect
                id={`key-credits-wrap-limit-${fieldName}`}
                value={String(currentWrapLimit)}
                label={t('common.wrap.limit')}
                onChange={handleWrapLimitChange}
                onKeyDown={stopKeyCreditsColumnMenuSelectKeyDown}
                onClick={stopKeyCreditsColumnMenuItemClick}
                onBlur={(event) => event.stopPropagation()}
                native={isBaseSelectNative}
                fullWidth
                size="small"
                style={{ minWidth: KEY_CREDITS_COLUMN_MENU_SELECT_MIN_WIDTH }}
            >
                {KEY_CREDITS_COLUMN_MENU_WRAP_LIMIT_OPTIONS.map((option) => (
                    <rootProps.slots.baseSelectOption key={option} native={isBaseSelectNative} value={String(option)}>
                        {option}
                    </rootProps.slots.baseSelectOption>
                ))}
            </rootProps.slots.baseSelect>
        </rootProps.slots.baseMenuItem>
    );
}
